import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  Rocket,
  Building2,
  ScrollText,
  NotebookText,
  ShieldPlus,
  BadgeCheck,
  UsersRound,
  Megaphone,
  FolderCog,
  MessagesSquare,
  ArrowRight,
  Loader2,
  CalendarCheck,
  Calendar as CalendarIcon,
  FileDown,
  Gift,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Reveal, FadeIn, Overline, GoldRule } from "@/components/Reveal";
import { Field, inputProps } from "@/components/Field";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const consultingServices = [
  { icon: Rocket, title: "Home Care Agency Startup Guidance", text: "Step-by-step guidance from idea to launch, so you start on solid ground." },
  { icon: Building2, title: "Business Setup & Organization", text: "Structure, registration, and organizing your business the right way from day one." },
  { icon: ScrollText, title: "Licensing Process Guidance", text: "Clear direction through your state's home care licensing requirements and paperwork." },
  { icon: NotebookText, title: "Policies & Procedures Guidance", text: "Build the policy manuals and procedures your agency needs to operate and stay compliant." },
  { icon: ShieldPlus, title: "Medicaid & Insurance Enrollment Guidance", text: "Navigate Medicaid and insurance enrollment so you can serve more clients and get paid." },
  { icon: BadgeCheck, title: "Credentialing Support", text: "Hands-on support through credentialing applications and approvals." },
  { icon: UsersRound, title: "Hiring & Staffing Setup", text: "Recruit, screen, and onboard compassionate caregivers with systems that scale." },
  { icon: Megaphone, title: "Marketing & Client Acquisition Strategies", text: "Practical strategies to build referrals, reach families, and grow your client base." },
  { icon: FolderCog, title: "Forms, Documents & Operational Setup", text: "The forms, templates, and day-to-day operational systems your agency runs on." },
  { icon: MessagesSquare, title: "One-on-One Consulting", text: "Personalized sessions focused on your agency's specific questions and challenges." },
];

const timeSlots = [
  "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
  "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM",
];

const Consulting = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(undefined);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", agency_name: "", state: "", assistance: "", preferred_time: "",
  });
  const [lead, setLead] = useState({ name: "", email: "" });
  const [leadLoading, setLeadLoading] = useState(false);

  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/consulting-requests`, {
        ...form,
        preferred_date: date ? format(date, "EEEE, MMMM d, yyyy") : "",
      });
      toast.success("Request received! Our consulting team will confirm your consultation shortly.");
      setForm({ name: "", email: "", phone: "", agency_name: "", state: "", assistance: "", preferred_time: "" });
      setDate(undefined);
      setOpen(false);
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    setLeadLoading(true);
    try {
      await axios.post(`${API}/checklist-leads`, lead);
      toast.success("Your checklist is downloading now!");
      const a = document.createElement("a");
      a.href = `${API}/downloads/startup-checklist`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setLead({ name: "", email: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLeadLoading(false);
    }
  };

  return (
    <main>
      <section data-testid="consulting-hero" className="relative overflow-hidden bg-blush-50 pb-16 pt-40 sm:pb-20">
        <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full border border-gold/25" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-32 -left-16 h-80 w-80 rounded-full border border-gold/20" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn>
            <Overline>Home Care Business Consulting</Overline>
          </FadeIn>
          <FadeIn delay={0.15}>
            <h1 className="mt-5 max-w-3xl font-serif text-4xl font-medium tracking-tight text-charcoal sm:text-5xl lg:text-6xl">
              Ready to Start Your <em className="text-gold-deep">Home Care Agency?</em>
            </h1>
          </FadeIn>
          <FadeIn delay={0.3}>
            <p className="mt-6 font-serif text-xl italic text-charcoal-light sm:text-2xl">
              You don't have to figure it out alone.
            </p>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-charcoal-muted sm:text-lg">
              New Beginnings Healthcare Services offers consulting and guidance
              for individuals who are starting a home care agency, newly
              licensed agency owners, and existing home care businesses that
              need additional guidance and support.
            </p>
          </FadeIn>
          <FadeIn delay={0.45} className="mt-10">
            <button
              onClick={() => setOpen(true)}
              data-testid="consulting-book-button-hero"
              className="btn-pink !px-9 !py-4 !text-sm"
            >
              <CalendarCheck className="h-4 w-4" /> Book a Consultation
            </button>
          </FadeIn>
        </div>
      </section>

      <section data-testid="consulting-services-section" className="bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="flex flex-col items-center text-center">
            <Overline centered>Consulting Services</Overline>
            <h2 className="mt-5 max-w-2xl font-serif text-3xl font-medium tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
              Guidance for every stage of your agency
            </h2>
            <GoldRule className="mt-8 justify-center" />
          </Reveal>
          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {consultingServices.map((s, i) => (
              <Reveal key={s.title} delay={(i % 3) * 0.08}>
                <div
                  data-testid="consulting-service-card"
                  className="group flex h-full flex-col rounded-3xl border border-blush-200/80 bg-white p-8 shadow-[0_2px_20px_rgba(31,41,55,0.04)] transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-gold/40 hover:shadow-[0_18px_44px_rgba(232,180,188,0.28)]"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-gold/30 bg-gold-soft transition-colors duration-300 group-hover:bg-blush-100">
                    <s.icon className="h-6 w-6 text-gold" strokeWidth={1.4} />
                  </span>
                  <h3 className="mt-6 font-serif text-xl text-charcoal">{s.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-charcoal-muted">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="checklist-section" className="bg-blush-50 py-20 sm:py-28">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2">
          <div>
            <Reveal>
              <Overline>Free Guide</Overline>
              <h2 className="mt-5 font-serif text-3xl font-medium tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
                Not ready to book yet? <em className="text-gold-deep">Start here.</em>
              </h2>
              <p className="mt-6 max-w-lg text-sm leading-relaxed text-charcoal-muted sm:text-base">
                Download our free <strong className="font-semibold text-charcoal">Home Care Agency Startup Checklist</strong> —
                ten essential steps covering licensing, policies, insurance,
                credentialing, hiring, and marketing, so you know exactly what
                to do first.
              </p>
            </Reveal>
            <Reveal delay={0.15} className="mt-8 space-y-3">
              {["Ten essential startup steps, in order", "Based on real agency launch experience", "Yours free — no obligation"].map((t) => (
                <div key={t} className="flex items-center gap-3 text-sm text-charcoal-light">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full border border-gold/40 bg-gold-soft">
                    <BadgeCheck className="h-3.5 w-3.5 text-gold" strokeWidth={1.8} />
                  </span>
                  {t}
                </div>
              ))}
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <form
              onSubmit={submitLead}
              data-testid="checklist-form"
              className="rounded-[2rem] border border-blush-200 bg-white p-7 shadow-[0_10px_44px_rgba(31,41,55,0.07)] sm:p-10"
            >
              <div className="flex items-center gap-4">
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-gold/30 bg-gold-soft">
                  <Gift className="h-7 w-7 text-gold" strokeWidth={1.3} />
                </span>
                <div>
                  <h3 className="font-serif text-2xl text-charcoal">Get the free checklist</h3>
                  <p className="mt-1 text-xs uppercase tracking-[0.16em] text-charcoal-faint">
                    Instant PDF download
                  </p>
                </div>
              </div>
              <div className="mt-7 grid gap-5">
                <Field label="Full name" htmlFor="cl-name" required>
                  <input id="cl-name" name="name" required value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} placeholder="Your name" {...inputProps("checklist-form-name-input")} />
                </Field>
                <Field label="Email" htmlFor="cl-email" required>
                  <input id="cl-email" name="email" type="email" required value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} placeholder="you@email.com" {...inputProps("checklist-form-email-input")} />
                </Field>
              </div>
              <button
                type="submit"
                data-testid="checklist-form-submit-button"
                disabled={leadLoading}
                className="btn-pink mt-7 w-full disabled:opacity-60"
              >
                {leadLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
                {leadLoading ? "Preparing..." : "Download the Free Checklist"}
              </button>
              <p className="mt-4 text-center text-xs text-charcoal-faint">
                We'll only send helpful startup resources. No spam, ever.
              </p>
            </form>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <Reveal>
            <div className="gold-frame bg-white px-6 py-16 text-center sm:px-16 sm:py-20">
              <span className="absolute -top-px left-1/2 h-px w-24 -translate-x-1/2 bg-gold" aria-hidden="true" />
              <span className="absolute -bottom-px left-1/2 h-px w-24 -translate-x-1/2 bg-gold" aria-hidden="true" />
              <GoldRule className="justify-center" />
              <h2 className="mt-6 font-serif text-3xl font-medium tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
                Let's build your agency, together
              </h2>
              <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-charcoal-muted sm:text-base">
                Book a consultation and get clear, practical next steps for your
                home care business — wherever you are in the journey.
              </p>
              <button
                onClick={() => setOpen(true)}
                data-testid="consulting-book-button-cta"
                className="btn-pink mt-9 !px-10 !py-4"
              >
                <CalendarCheck className="h-4 w-4" /> Book a Consultation
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="consulting-form-dialog"
          className="max-h-[90vh] overflow-y-auto rounded-3xl border-blush-200 bg-white sm:max-w-lg"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-charcoal">
              Book a Consultation
            </DialogTitle>
            <DialogDescription className="text-sm text-charcoal-muted">
              Tell us about your business goals — we'll reach out to schedule
              your consultation.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} data-testid="consulting-form" className="mt-2 grid gap-5">
            <Field label="Full name" htmlFor="cs-name" required>
              <input id="cs-name" name="name" required value={form.name} onChange={update} placeholder="Your name" {...inputProps("consulting-form-name-input")} />
            </Field>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Email" htmlFor="cs-email" required>
                <input id="cs-email" name="email" type="email" required value={form.email} onChange={update} placeholder="you@email.com" {...inputProps("consulting-form-email-input")} />
              </Field>
              <Field label="Phone" htmlFor="cs-phone" required>
                <input id="cs-phone" name="phone" type="tel" required value={form.phone} onChange={update} placeholder="(856) 555-0100" {...inputProps("consulting-form-phone-input")} />
              </Field>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Agency / Business name" htmlFor="cs-agency">
                <input id="cs-agency" name="agency_name" value={form.agency_name} onChange={update} placeholder="Your agency (if any)" {...inputProps("consulting-form-agency-input")} />
              </Field>
              <Field label="State" htmlFor="cs-state" required>
                <input id="cs-state" name="state" required value={form.state} onChange={update} placeholder="e.g. New Jersey" {...inputProps("consulting-form-state-input")} />
              </Field>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <span className="field-label">Preferred date</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="consulting-form-date-button"
                      className={`field-input flex items-center justify-between text-left ${!date ? "text-charcoal-faint/60" : ""}`}
                    >
                      {date ? format(date, "MMM d, yyyy") : "Pick a date"}
                      <CalendarIcon className="h-4 w-4 shrink-0 text-gold" strokeWidth={1.5} />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto border-blush-200 p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={{ before: new Date() }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <Field label="Preferred time" htmlFor="cs-time">
                <select id="cs-time" name="preferred_time" value={form.preferred_time} onChange={update} {...inputProps("consulting-form-time-select")}>
                  <option value="">Select a time</option>
                  {timeSlots.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
            </div>
            <Field label="What do you need assistance with?" htmlFor="cs-assistance" required>
              <textarea id="cs-assistance" name="assistance" rows={4} required value={form.assistance} onChange={update} placeholder="e.g. I'm starting an agency and need help with licensing and policies..." {...inputProps("consulting-form-assistance-textarea")} />
            </Field>
            <button
              type="submit"
              data-testid="consulting-form-submit-button"
              disabled={loading}
              className="btn-pink w-full disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
              {loading ? "Sending..." : "Book a Consultation"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </main>
  );
};

export default Consulting;
